import React, { useState, useMemo } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, ArrowLeft, Search, Inbox } from 'lucide-react';
import { Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Input } from '@/components/ui/input';
import EmailInboxItem from '@/components/admin/email/EmailInboxItem';
import EmailThreadPanel from '@/components/admin/email/EmailThreadPanel';
import {
  buildEmailConversations,
  threadMessages,
  threadHasUnread,
  threadKey,
  isUnreadReply,
} from '@/lib/emailThreads';
import AdminSectionTabs from '@/components/admin/AdminSectionTabs';

// The email and SMS inboxes are one destination in the sidebar, joined by this
// strip. Each keeps its own route, data and actions.
const INBOX_TABS = [
  { to: '/admin/email-inbox', label: 'EMAIL INBOX' },
  { to: '/admin/sms-inbox', label: 'SMS INBOX' },
];

// Triage inbox for inbound customer email replies — a sibling of the SMS inbox.
// Unread-first list on the left, the full conversation and its reply composer
// on the right.
export default function AdminEmailInbox() {
  const { user: currentUser, isLoadingAuth } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('unread'); // 'unread' | 'all'
  const [selected, setSelected] = useState(null);

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['admin-email-inbox'],
    queryFn: () => base44.entities.CustomerEmailLog.list('-created_date', 500),
    enabled: currentUser?.role === 'admin',
  });

  const conversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    return buildEmailConversations(logs).filter((c) => {
      const matchesSearch = !q ||
        (c.customer_name || '').toLowerCase().includes(q) ||
        (c.from_address || '').toLowerCase().includes(q) ||
        (c.customer_email || '').toLowerCase().includes(q) ||
        (c.subject || '').toLowerCase().includes(q);
      const matchesFilter = filter === 'all' || threadHasUnread(logs, threadKey(c));
      return matchesSearch && matchesFilter;
    });
  }, [logs, search, filter]);

  const unreadCount = useMemo(() => logs.filter(isUnreadReply).length, [logs]);

  const handleSelect = async (message) => {
    setSelected(message);
    try {
      await base44.entities.CustomerEmailLog.updateMany(
        { thread_id: message.thread_id || message.id, direction: 'inbound', inbox_status: 'new' },
        { $set: { inbox_status: 'read' } }
      );
      queryClient.invalidateQueries({ queryKey: ['admin-email-inbox'] });
    } catch (e) {
      console.error('Failed to mark read:', e);
    }
  };

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const selectedKey = selected ? threadKey(selected) : null;
  const selectedMessages = selectedKey ? threadMessages(logs, selectedKey) : [];

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">ORDERS &amp; CUSTOMERS</p>
            <h1 className="text-white font-black text-2xl sm:text-3xl md:text-4xl uppercase">EMAIL INBOX</h1>
            <p className="text-white/30 text-sm mt-1">
              {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
              {unreadCount > 0 && ` · ${unreadCount} unread`}
            </p>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 pb-16 max-w-6xl mx-auto">
        <AdminSectionTabs tabs={INBOX_TABS} className="mb-6" />
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or subject..."
              className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60"
            />
          </div>
          <div className="flex gap-1 border border-white/10">
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2.5 text-[10px] font-bold tracking-widest transition-colors ${filter === 'unread' ? 'bg-primary text-white' : 'text-white/40 hover:text-white'}`}
            >
              UNREAD{unreadCount > 0 ? ` (${unreadCount})` : ''}
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2.5 text-[10px] font-bold tracking-widest transition-colors ${filter === 'all' ? 'bg-primary text-white' : 'text-white/40 hover:text-white'}`}
            >
              ALL
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Inbox className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">No email replies {filter === 'unread' ? 'unread' : 'found'}.</p>
            <p className="text-white/20 text-xs mt-1">
              Customer replies to CRM emails will appear here once the receiving mailbox is connected.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className={`lg:col-span-2 space-y-2 ${selected ? 'hidden lg:block' : 'block'}`}>
              {conversations.map((c) => {
                const key = threadKey(c);
                return (
                  <EmailInboxItem
                    key={key}
                    message={c}
                    unread={threadHasUnread(logs, key)}
                    selected={selectedKey === key}
                    onSelect={handleSelect}
                  />
                );
              })}
            </div>

            {selected && (
              <div className="lg:col-span-3">
                <button
                  onClick={() => setSelected(null)}
                  className="lg:hidden mb-3 text-white/40 hover:text-white text-[10px] font-bold tracking-widest border border-white/10 px-3 py-1.5 transition-colors"
                >
                  BACK
                </button>
                <EmailThreadPanel
                  message={selected}
                  messages={selectedMessages}
                  onChanged={() => queryClient.invalidateQueries({ queryKey: ['admin-email-inbox'] })}
                />
              </div>
            )}

            {!selected && conversations.length > 0 && (
              <div className="hidden lg:flex lg:col-span-3 items-center justify-center border border-white/5 bg-[#111115] min-h-[400px]">
                <div className="text-center">
                  <Inbox className="w-8 h-8 text-white/10 mx-auto mb-3" />
                  <p className="text-white/30 text-xs">Select a reply to view the conversation</p>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}